import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FaArrowLeft, FaSave, FaCheckCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { createChart, ColorType, CrosshairMode, LineStyle } from 'lightweight-charts';
import { toast } from 'react-toastify';
import screenerApi, { ScreenerMasterResponse, ScreenerStockResponse } from '../../../services/screenerApi.ts';
import './AdminChartDrawing.css';

type DrawingTool = 'CURSOR' | 'TREND_LINE' | 'ARROW' | 'H_LINE' | 'V_LINE' | 'H_RAY_RIGHT' | 'H_RAY_LEFT' | 'RECTANGLE' | 'FREEHAND' | 'ARC' | 'TEXT';

interface ChartPoint { time: string; price: number; }

interface Drawing {
  id: string;
  type: DrawingTool;
  points: ChartPoint[];
  color: string;
  lineWidth: number;
  text?: string;
}

const TOOLS: { id: DrawingTool; label: string; icon: string; tip: string }[] = [
  { id: 'CURSOR',     icon: '↖',  label: 'Cursor',     tip: 'Select / pan chart' },
  { id: 'TREND_LINE', icon: '╱',  label: 'Trend Line', tip: 'Click 2 points' },
  { id: 'ARROW',      icon: '→',  label: 'Arrow',      tip: 'Click start → end' },
  { id: 'H_LINE',      icon: '─',  label: 'H-Line',    tip: 'Click 1 point' },
  { id: 'V_LINE',      icon: '│',  label: 'V-Line',    tip: 'Click 1 point' },
  { id: 'H_RAY_RIGHT', icon: '⊢',  label: 'Ray →',     tip: 'Click 1 point, extends right' },
  { id: 'H_RAY_LEFT',  icon: '⊣',  label: 'Ray ←',     tip: 'Click 1 point, extends left' },
  { id: 'RECTANGLE',  icon: '▭',  label: 'Rectangle',  tip: 'Click & drag' },
  { id: 'ARC',        icon: '⌒',  label: 'Arc',        tip: 'Click start → end → apex' },
  { id: 'FREEHAND',   icon: '✏',  label: 'Free-hand',  tip: 'Click & drag freely' },
  { id: 'TEXT',       icon: 'T',  label: 'Text',       tip: 'Click and type' },
];

// ── Arc helper ────────────────────────────────────────────────────────────
// Draws a half-ellipse arc from p0 to p1 that passes through apex.
// The apex is a real chart coordinate, so the arc is zoom-stable.
function drawEllipseArc(
  ctx: CanvasRenderingContext2D,
  p0: { x: number; y: number },   // left endpoint (pixels)
  p1: { x: number; y: number },   // right endpoint (pixels)
  apex: { x: number; y: number }, // apex point (pixels)
) {
  const adx = p1.x - p0.x;
  const ady = p1.y - p0.y;
  const chordLen = Math.hypot(adx, ady);
  if (chordLen < 1) return;
  const perpX = -ady / chordLen;
  const perpY =  adx / chordLen;
  const midX = (p0.x + p1.x) / 2;
  const midY = (p0.y + p1.y) / 2;
  const h = (apex.x - midX) * perpX + (apex.y - midY) * perpY;
  if (Math.abs(h) < 0.5) return;
  ctx.ellipse(midX, midY, chordLen / 2, Math.abs(h), Math.atan2(ady, adx), Math.PI, 0, h < 0);
}

const COLORS = ['#FF6B00', '#2962FF', '#26a69a', '#ef5350', '#9C27B0', '#FFD600', '#fff', '#aaaaaa'];

const AdminChartDrawing: React.FC = () => {
  const navigate = useNavigate();

  // ── Screeners & Stocks ───────────────────────────────────────────────
  const [screeners, setScreeners]             = useState<ScreenerMasterResponse[]>([]);
  const [selectedScreener, setSelectedScreener] = useState<string>('');
  const [stocks, setStocks]                   = useState<ScreenerStockResponse[]>([]);
  const [loadingStocks, setLoadingStocks]     = useState(false);
  const [selectedStock, setSelectedStock]     = useState<ScreenerStockResponse | null>(null);

  // ── Stock with candles (fetched from chart API since admin endpoint omits them) ──
  const [chartStock, setChartStock]       = useState<ScreenerStockResponse | null>(null);
  const [loadingCandles, setLoadingCandles] = useState(false);

  // ── Save state ─────────────────────────────────────────────────────
  const [saving, setSaving]               = useState(false);
  const [savedOk, setSavedOk]             = useState(false);

  // ── OHLC header (updates on crosshair move) ──────────────────────────
  const [priceData, setPriceData] = useState({ open: 0, high: 0, low: 0, close: 0, prevClose: 0 });

  // ── Drawing state ─────────────────────────────────────────────────────
  const [activeTool, setActiveTool]       = useState<DrawingTool>('CURSOR');
  const [drawings, setDrawings]           = useState<Drawing[]>([]);
  const [activeColor, setActiveColor]     = useState('#FF6B00');
  const [lineWidth, setLineWidth]         = useState(2);

  // Text tool input state
  const [pendingText, setPendingText]     = useState<{ pos: ChartPoint; px: number; py: number } | null>(null);
  const [textValue, setTextValue]         = useState('');
  const textInputRef                      = useRef<HTMLInputElement>(null);

  // In-progress drawing (before completion)
  const inProgressPoints  = useRef<ChartPoint[]>([]);
  const isMouseDown       = useRef(false);
  const freehandPoints    = useRef<ChartPoint[]>([]);
  const rectStartPx       = useRef<{ x: number; y: number } | null>(null);
  const mousePixel        = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // ── Chart refs ────────────────────────────────────────────────────────
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const canvasRef         = useRef<HTMLCanvasElement>(null);
  const chartRef          = useRef<any>(null);
  const seriesRef         = useRef<any>(null);
  const drawingsRef       = useRef<Drawing[]>([]);
  drawingsRef.current     = drawings;
  // Handle dragging (CURSOR tool) — no React state so dragging is smooth
  const draggingHandle  = useRef<{ drawingId: string; handleIdx: number } | null>(null);
  const dragPreviewRef  = useRef<Drawing | null>(null); // live copy during drag

  // ── Load screeners on mount ───────────────────────────────────────────
  useEffect(() => {
    screenerApi.getScreeners()
      .then(res => {
        setScreeners(res.data);
        if (res.data.length > 0) setSelectedScreener(res.data[0].code);
      })
      .catch(() => toast.error('Failed to load screeners'));
  }, []);

  // ── Load stocks when screener changes ─────────────────────────────────
  useEffect(() => {
    if (!selectedScreener) return;
    setLoadingStocks(true);
    setSelectedStock(null);
    setStocks([]);
    screenerApi.getAdminStocks(selectedScreener, 1, 100)
      .then(res => {
        setStocks(res.data.content);
        if (res.data.content.length > 0) setSelectedStock(res.data.content[0]);
      })
      .catch(() => toast.error('Failed to load stocks'))
      .finally(() => setLoadingStocks(false));
  }, [selectedScreener]);

  // ── Load drawings when stock changes ─────────────────────────────────
  useEffect(() => {
    if (!selectedStock) return;
    screenerApi.getDrawings(selectedStock.id)
      .then(res => {
        const loaded = Array.isArray(res.data.drawings) ? res.data.drawings as Drawing[] : [];
        setDrawings(loaded);
      })
      .catch(() => setDrawings([]));
    setSavedOk(false);
  }, [selectedStock?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch candles when stock changes (admin endpoint doesn't include them) ──
  useEffect(() => {
    if (!selectedStock) { setChartStock(null); return; }
    if (selectedStock.candles?.length) {
      setChartStock(selectedStock);
      return;
    }
    setChartStock(null);
    setLoadingCandles(true);
    screenerApi.getStockCandles(selectedStock.symbol)
      .then(res => setChartStock({ ...selectedStock, candles: res.data.candles }))
      .catch(() => setChartStock({ ...selectedStock, candles: [] }))
      .finally(() => setLoadingCandles(false));
  }, [selectedStock?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save drawings ─────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedStock) return;
    setSaving(true);
    try {
      await screenerApi.saveDrawings(selectedStock.id, drawings);
      setSavedOk(true);
      toast.success(`Drawings saved for ${selectedStock.symbol}`);
      setTimeout(() => setSavedOk(false), 3000);
    } catch {
      toast.error('Failed to save drawings');
    } finally {
      setSaving(false);
    }
  };

  // ── Coordinate helpers ────────────────────────────────────────────────
  const toPixel = useCallback((time: string, price: number) => {
    if (!chartRef.current || !seriesRef.current) return null;
    const x = chartRef.current.timeScale().timeToCoordinate(time);
    const y = seriesRef.current.priceToCoordinate(price);
    if (x === null || y === null) return null;
    return { x: Math.round(x), y: Math.round(y) };
  }, []);

  const toChartCoords = useCallback((pixelX: number, pixelY: number): ChartPoint | null => {
    if (!chartRef.current || !seriesRef.current) return null;
    const time  = chartRef.current.timeScale().coordinateToTime(pixelX);
    const price = seriesRef.current.coordinateToPrice(pixelY);
    if (!time || price === null) return null;
    return { time: time as string, price };
  }, []);

  // ── Canvas draw ───────────────────────────────────────────────────────
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const render = (d: Drawing, previewEndPx?: { x: number; y: number }) => {
      if (!chartRef.current || !seriesRef.current) return;
      ctx.strokeStyle = d.color;
      ctx.lineWidth   = Math.max(1, d.lineWidth);
      ctx.globalAlpha = 1;
      ctx.setLineDash([]);

      switch (d.type) {
        case 'H_LINE': {
          const y = seriesRef.current.priceToCoordinate(d.points[0].price);
          if (y === null) break;
          ctx.beginPath();
          ctx.moveTo(0, Math.round(y));
          ctx.lineTo(canvas.width, Math.round(y));
          ctx.stroke();
          break;
        }
        case 'V_LINE': {
          const x = chartRef.current.timeScale().timeToCoordinate(d.points[0].time);
          if (x === null) break;
          ctx.beginPath();
          ctx.moveTo(Math.round(x), 0);
          ctx.lineTo(Math.round(x), canvas.height);
          ctx.stroke();
          break;
        }
        case 'H_RAY_RIGHT': {
          const y = seriesRef.current.priceToCoordinate(d.points[0].price);
          const x = chartRef.current.timeScale().timeToCoordinate(d.points[0].time);
          if (y == null || x == null) break;
          ctx.beginPath();
          ctx.moveTo(Math.round(x), Math.round(y));
          ctx.lineTo(canvas.width, Math.round(y));
          ctx.stroke();
          break;
        }
        case 'H_RAY_LEFT': {
          const y = seriesRef.current.priceToCoordinate(d.points[0].price);
          const x = chartRef.current.timeScale().timeToCoordinate(d.points[0].time);
          if (y == null || x == null) break;
          ctx.beginPath();
          ctx.moveTo(0, Math.round(y));
          ctx.lineTo(Math.round(x), Math.round(y));
          ctx.stroke();
          break;
        }
        case 'TEXT': {
          const tx = chartRef.current.timeScale().timeToCoordinate(d.points[0].time);
          const ty = seriesRef.current.priceToCoordinate(d.points[0].price);
          if (tx === null || ty === null) break;
          ctx.fillStyle = d.color;
          ctx.font = `bold ${10 + d.lineWidth * 3}px sans-serif`;
          ctx.textBaseline = 'middle';
          ctx.fillText(d.text ?? '', Math.round(tx), Math.round(ty));
          break;
        }
        default: {
          // Tools that need both time+price for every point
          const pts = d.points.map(p => toPixel(p.time, p.price)).filter(Boolean) as { x: number; y: number }[];

          switch (d.type) {
            case 'TREND_LINE': {
              const p0 = pts[0];
              const p1 = previewEndPx ?? pts[1];
              if (!p0 || !p1) break;
              ctx.beginPath();
              ctx.moveTo(p0.x, p0.y);
              ctx.lineTo(p1.x, p1.y);
              ctx.stroke();
              if (previewEndPx) {
                ctx.beginPath();
                ctx.arc(p0.x, p0.y, 4, 0, Math.PI * 2);
                ctx.fillStyle = d.color;
                ctx.fill();
              }
              break;
            }
            case 'ARROW': {
              const p0 = pts[0];
              const p1 = previewEndPx ?? pts[1];
              if (!p0 || !p1) break;
              const angle   = Math.atan2(p1.y - p0.y, p1.x - p0.x);
              const headLen = Math.max(12, d.lineWidth * 5);
              // Line
              ctx.beginPath();
              ctx.moveTo(p0.x, p0.y);
              ctx.lineTo(p1.x, p1.y);
              ctx.stroke();
              // Filled arrowhead at p1
              ctx.beginPath();
              ctx.moveTo(p1.x, p1.y);
              ctx.lineTo(
                p1.x - headLen * Math.cos(angle - Math.PI / 6),
                p1.y - headLen * Math.sin(angle - Math.PI / 6),
              );
              ctx.lineTo(
                p1.x - headLen * Math.cos(angle + Math.PI / 6),
                p1.y - headLen * Math.sin(angle + Math.PI / 6),
              );
              ctx.closePath();
              ctx.fillStyle = d.color;
              ctx.fill();
              if (previewEndPx) {
                ctx.beginPath();
                ctx.arc(p0.x, p0.y, 4, 0, Math.PI * 2);
                ctx.fillStyle = d.color;
                ctx.fill();
              }
              break;
            }
            case 'RECTANGLE': {
              const p0 = pts[0];
              const p1 = previewEndPx ?? pts[1];
              if (!p0 || !p1) break;
              const rx = Math.min(p0.x, p1.x);
              const ry = Math.min(p0.y, p1.y);
              const rw = Math.abs(p1.x - p0.x);
              const rh = Math.abs(p1.y - p0.y);
              ctx.strokeRect(rx, ry, rw, rh);
              ctx.save();
              ctx.globalAlpha = 0.1;
              ctx.fillStyle = d.color;
              ctx.fillRect(rx, ry, rw, rh);
              ctx.restore();
              break;
            }
            case 'ARC': {
              if (pts.length < 3) break;
              // Fill chord segment
              ctx.beginPath();
              drawEllipseArc(ctx, pts[0], pts[1], pts[2]);
              ctx.closePath();
              ctx.save();
              ctx.globalAlpha = 0.1;
              ctx.fillStyle = d.color;
              ctx.fill();
              ctx.restore();
              // Stroke arc
              ctx.beginPath();
              drawEllipseArc(ctx, pts[0], pts[1], pts[2]);
              ctx.stroke();
              break;
            }
            case 'FREEHAND': {
              if (pts.length < 2) break;
              ctx.lineJoin = 'round'; ctx.lineCap = 'round';
              ctx.beginPath();
              ctx.moveTo(pts[0].x, pts[0].y);
              for (let i = 1; i < pts.length - 1; i++) {
                const mx = (pts[i].x + pts[i + 1].x) / 2;
                const my = (pts[i].y + pts[i + 1].y) / 2;
                ctx.quadraticCurveTo(pts[i].x, pts[i].y, mx, my);
              }
              ctx.lineTo(pts[pts.length - 1].x, pts[pts.length - 1].y);
              ctx.stroke();
              break;
            }
          }
        }
      }
    };

    // Render saved drawings (use live drag preview when a handle is being dragged)
    drawingsRef.current.forEach(d => {
      const live = dragPreviewRef.current;
      render(live && live.id === d.id ? live : d);
    });

    // Render in-progress preview
    const tool = activeTool;
    if ((tool === 'TREND_LINE' || tool === 'ARROW') && inProgressPoints.current.length === 1) {
      render(
        { id: '__preview', type: tool, points: inProgressPoints.current, color: activeColor, lineWidth },
        mousePixel.current
      );
    }
    if (tool === 'RECTANGLE' && rectStartPx.current) {
      const startPx = rectStartPx.current;
      const curPx   = mousePixel.current;
      ctx.strokeStyle = activeColor;
      ctx.lineWidth   = lineWidth;
      ctx.setLineDash([4, 4]);
      const rx = Math.min(startPx.x, curPx.x);
      const ry = Math.min(startPx.y, curPx.y);
      const rw = Math.abs(curPx.x - startPx.x);
      const rh = Math.abs(curPx.y - startPx.y);
      ctx.strokeRect(rx, ry, rw, rh);
      ctx.save();
      ctx.globalAlpha = 0.08;
      ctx.fillStyle = activeColor;
      ctx.fillRect(rx, ry, rw, rh);
      ctx.restore();
      ctx.setLineDash([]);
    }
    if (tool === 'FREEHAND' && freehandPoints.current.length > 1) {
      const pts2 = freehandPoints.current.map(p => toPixel(p.time, p.price)).filter(Boolean) as { x: number; y: number }[];
      if (pts2.length > 1) {
        ctx.strokeStyle = activeColor;
        ctx.lineWidth   = lineWidth;
        ctx.lineJoin    = 'round';
        ctx.lineCap     = 'round';
        ctx.beginPath();
        ctx.moveTo(pts2[0].x, pts2[0].y);
        for (let i = 1; i < pts2.length - 1; i++) {
          const mx = (pts2[i].x + pts2[i + 1].x) / 2;
          const my = (pts2[i].y + pts2[i + 1].y) / 2;
          ctx.quadraticCurveTo(pts2[i].x, pts2[i].y, mx, my);
        }
        ctx.lineTo(pts2[pts2.length - 1].x, pts2[pts2.length - 1].y);
        ctx.stroke();
      }
    }

    // ARC preview — 3-click tool: start → end → apex
    if (tool === 'ARC' && inProgressPoints.current.length >= 1) {
      const stored = inProgressPoints.current
        .map(p => toPixel(p.time, p.price))
        .filter(Boolean) as { x: number; y: number }[];
      const mouse = mousePixel.current;
      ctx.strokeStyle = activeColor;
      ctx.lineWidth   = lineWidth;
      ctx.setLineDash([5, 4]);

      // Draw dot for each stored point
      stored.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = activeColor;
        ctx.globalAlpha = 0.8;
        ctx.fill();
        ctx.globalAlpha = 1;
      });

      if (stored.length === 1) {
        // After click 1 (start) — line to mouse showing where end will go
        ctx.beginPath();
        ctx.moveTo(stored[0].x, stored[0].y);
        ctx.lineTo(mouse.x, mouse.y);
        ctx.stroke();
      } else if (stored.length === 2) {
        // After click 2 (end fixed) — live half-ellipse preview; mouse is the apex.
        ctx.beginPath();
        drawEllipseArc(ctx, stored[0], stored[1], mouse);
        ctx.closePath();
        ctx.save();
        ctx.globalAlpha = 0.07;
        ctx.fillStyle = activeColor;
        ctx.fill();
        ctx.restore();
        ctx.beginPath();
        drawEllipseArc(ctx, stored[0], stored[1], mouse);
        ctx.stroke();
      }
      ctx.setLineDash([]);
    }

    // ── Draw drag handles on every drawing when CURSOR is active ──────────
    if (activeTool === 'CURSOR') {
      const HR = 5; // handle radius px
      drawingsRef.current.forEach(d => {
        const live = dragPreviewRef.current?.id === d.id ? dragPreviewRef.current! : d;
        const handles: Array<{ x: number; y: number } | null> = [];

        if (live.type === 'ARC' && live.points.length >= 2) {
          live.points.forEach(p => handles.push(toPixel(p.time, p.price)));
        } else if (live.type === 'TREND_LINE' || live.type === 'ARROW' || live.type === 'RECTANGLE') {
          live.points.forEach(p => handles.push(toPixel(p.time, p.price)));
        } else if (live.type === 'H_LINE') {
          const y = seriesRef.current?.priceToCoordinate(live.points[0].price);
          if (y != null) handles.push({ x: canvas.width / 2, y: Math.round(y) });
        } else if (live.type === 'H_RAY_RIGHT' || live.type === 'H_RAY_LEFT') {
          handles.push(toPixel(live.points[0].time, live.points[0].price));
        } else if (live.type === 'V_LINE') {
          const x = chartRef.current?.timeScale().timeToCoordinate(live.points[0].time);
          if (x != null) handles.push({ x: Math.round(x), y: canvas.height / 2 });
        } else if (live.type === 'TEXT') {
          handles.push(toPixel(live.points[0].time, live.points[0].price));
        }

        ctx.setLineDash([]);
        handles.forEach((h, i) => {
          if (!h) return;
          const active = draggingHandle.current?.drawingId === d.id
                      && draggingHandle.current.handleIdx === i;
          ctx.beginPath();
          ctx.arc(h.x, h.y, active ? HR + 2 : HR, 0, Math.PI * 2);
          ctx.globalAlpha   = 1;
          ctx.fillStyle     = active ? live.color : 'rgba(255,255,255,0.92)';
          ctx.strokeStyle   = live.color;
          ctx.lineWidth     = 1.5;
          ctx.fill();
          ctx.stroke();
        });
      });
    }
  }, [activeTool, activeColor, lineWidth, toPixel]);

  // Always points to the latest redrawCanvas so chart subscriptions never
  // call a stale closure captured at stock-selection time.
  const redrawRef = useRef(redrawCanvas);
  redrawRef.current = redrawCanvas;

  // ── Build chart when chartStock (stock + candles) is ready ───────────
  useEffect(() => {
    if (!chartContainerRef.current || !chartStock?.candles?.length) return;

    // Destroy previous chart
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
      seriesRef.current = null;
    }
    inProgressPoints.current = [];
    freehandPoints.current   = [];
    rectStartPx.current      = null;

    const container = chartContainerRef.current;
    const chartWidth = container.getBoundingClientRect().width || container.clientWidth;
    const chart = createChart(container, {
      width:  chartWidth,
      height: 500,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor:  '#d1d4dc',
      },
      grid: {
        vertLines: { color: 'rgba(197,203,206,0.1)' },
        horzLines: { color: 'rgba(197,203,206,0.1)' },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: 'rgba(197,203,206,0.3)' },
      timeScale: {
        borderColor:    'rgba(197,203,206,0.3)',
        rightOffset:    8,
        barSpacing:     10,
      },
    });
    chartRef.current = chart;

    const series = (chart as any).addCandlestickSeries({
      upColor:      '#26a69a',
      downColor:    '#ef5350',
      borderVisible: false,
      wickUpColor:  '#26a69a',
      wickDownColor:'#ef5350',
    });
    seriesRef.current = series;

    const data = chartStock.candles!.map(c => ({
      time: c.time, open: c.open, high: c.high, low: c.low, close: c.close,
    }));
    series.setData(data);

    // Expand auto-scale range so resistance/support price lines stay visible
    if (chartStock.resistanceLevel != null || chartStock.supportLevel != null) {
      const resistanceLevel = chartStock.resistanceLevel;
      const supportLevel = chartStock.supportLevel;
      series.applyOptions({
        autoscaleInfoProvider: (original: () => any) => {
          const res = original();
          if (!res || !res.priceRange) return res;
          let { minValue, maxValue } = res.priceRange;
          if (resistanceLevel != null) {
            minValue = Math.min(minValue, resistanceLevel);
            maxValue = Math.max(maxValue, resistanceLevel);
          }
          if (supportLevel != null) {
            minValue = Math.min(minValue, supportLevel);
            maxValue = Math.max(maxValue, supportLevel);
          }
          return { ...res, priceRange: { minValue, maxValue } };
        },
      });
    }

    // Resistance/Support reference lines (educational reference only, not investment advice)
    if (chartStock.resistanceLevel != null) {
      series.createPriceLine({
        price: chartStock.resistanceLevel,
        color: '#f59e0b',
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: 'Resistance',
      });
    }
    if (chartStock.supportLevel != null) {
      series.createPriceLine({
        price: chartStock.supportLevel,
        color: '#3b82f6',
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: 'Support',
      });
    }

    // Volume
    const vol = (chart as any).addHistogramSeries({
      priceFormat: { type: 'volume' },
      priceScaleId: '',
    });
    vol.priceScale().applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
    vol.setData(chartStock.candles!.map(c => ({
      time:  c.time,
      value: c.volume || 0,
      color: c.close > c.open ? 'rgba(38,166,154,0.5)' : 'rgba(239,83,80,0.5)',
    })));

    // OHLC header data
    const last = data[data.length - 1];
    const prev = data[data.length - 2];
    setPriceData({ open: last.open, high: last.high, low: last.low, close: last.close, prevClose: prev?.close ?? last.close });

    chart.subscribeCrosshairMove((param: any) => {
      if (!param?.time) {
        setPriceData({ open: last.open, high: last.high, low: last.low, close: last.close, prevClose: prev?.close ?? last.close });
        return;
      }
      if (param.seriesData?.size > 0) {
        const d = param.seriesData.values().next().value;
        if (d?.open !== undefined) {
          const i = data.findIndex((c: any) => c.time === param.time);
          setPriceData({ open: d.open, high: d.high, low: d.low, close: d.close, prevClose: i > 0 ? data[i - 1].close : d.close });
        }
      }
    });

    // Redraw canvas when chart pans/zooms
    // Use redrawRef so these subscriptions always call the latest redrawCanvas,
    // even after tool/color changes create a new closure reference.
    chart.timeScale().subscribeVisibleLogicalRangeChange(() => redrawRef.current());
    chart.timeScale().subscribeVisibleTimeRangeChange(()   => redrawRef.current());
    // v4.1.x has no price-scale subscription API.  A native mousemove listener on
    // the container fires continuously during price-scale drag (and scroll/pan),
    // keeping the overlay in sync without any polling.
    const onContainerMove = () => redrawRef.current();
    container.addEventListener('mousemove', onContainerMove);

    // Sync canvas size with chart (use getBoundingClientRect so it matches exactly)
    const syncCanvasSize = () => {
      if (!chartContainerRef.current) return;
      const w = Math.round(chartContainerRef.current.getBoundingClientRect().width) || chartContainerRef.current.clientWidth;
      if (canvasRef.current) {
        canvasRef.current.width  = w;
        canvasRef.current.height = 500;
      }
      if (chartRef.current) chartRef.current.applyOptions({ width: w });
    };
    syncCanvasSize();

    const ro = new ResizeObserver(() => { syncCanvasSize(); redrawCanvas(); });
    ro.observe(container);

    return () => {
      ro.disconnect();
      container.removeEventListener('mousemove', onContainerMove);
      chart.remove();
      chartRef.current  = null;
      seriesRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartStock]);

  // Re-render canvas when drawings / tool / color change
  useEffect(() => { redrawCanvas(); }, [redrawCanvas, drawings, activeTool, activeColor, lineWidth]);

  // ── CURSOR mode: native capture-phase listeners on the chart container ──
  // Canvas has pointerEvents:none in CURSOR mode so the chart can pan normally.
  // We intercept events at the container level (capture phase) to handle dragging.
  // When a handle is hit we stopPropagation so the chart doesn't also pan.
  useEffect(() => {
    if (activeTool !== 'CURSOR') return;
    const container = chartContainerRef.current;
    if (!container) return;

    const onDown = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const HIT = 10;
      for (const d of drawingsRef.current) {
        const handles = getCursorHandles(d);
        for (let i = 0; i < handles.length; i++) {
          const h = handles[i];
          if (!h) continue;
          if (Math.hypot(px - h.x, py - h.y) <= HIT) {
            e.stopPropagation();   // prevent chart from starting a pan
            draggingHandle.current  = { drawingId: d.id, handleIdx: i };
            dragPreviewRef.current  = d;
            isMouseDown.current     = true;
            return;
          }
        }
      }
    };

    const onMove = (e: MouseEvent) => {
      if (!draggingHandle.current || !isMouseDown.current || !dragPreviewRef.current) return;
      e.stopPropagation();
      const rect = container.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      dragPreviewRef.current = applyHandleDrag(
        dragPreviewRef.current, draggingHandle.current.handleIdx, px, py,
      );
      redrawCanvas();
    };

    const onUp = () => {
      if (dragPreviewRef.current) {
        const preview = dragPreviewRef.current;
        setDrawings(prev => prev.map(d => d.id === preview.id ? preview : d));
      }
      draggingHandle.current = null;
      dragPreviewRef.current = null;
      isMouseDown.current    = false;
      redrawCanvas();
    };

    container.addEventListener('mousedown',  onDown, true);
    container.addEventListener('mousemove',  onMove, true);
    container.addEventListener('mouseup',    onUp,   true);
    container.addEventListener('mouseleave', onUp);

    return () => {
      container.removeEventListener('mousedown',  onDown, true);
      container.removeEventListener('mousemove',  onMove, true);
      container.removeEventListener('mouseup',    onUp,   true);
      container.removeEventListener('mouseleave', onUp);
    };
  // getCursorHandles / applyHandleDrag read only from stable refs — no extra deps needed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTool, redrawCanvas]);

  // ── Helpers ───────────────────────────────────────────────────────────
  // Always compute coords relative to the chart container (what the chart API expects).
  // Using the canvas rect instead would cause a coordinate mismatch if they're offset.
  const getEventCoords = (e: React.MouseEvent) => {
    if (!chartContainerRef.current) return { px: 0, py: 0 };
    const rect = chartContainerRef.current.getBoundingClientRect();
    return { px: e.clientX - rect.left, py: e.clientY - rect.top };
  };

  // ── Handle helpers (CURSOR tool) ─────────────────────────────────────
  // Returns pixel positions of all drag handles for a drawing.
  const getCursorHandles = (d: Drawing): Array<{ x: number; y: number } | null> => {
    if (!chartRef.current || !seriesRef.current || !canvasRef.current) return [];
    if (d.type === 'ARC') {
      return d.points.map(p => toPixel(p.time, p.price));
    }
    if (d.type === 'TREND_LINE' || d.type === 'ARROW' || d.type === 'RECTANGLE')
      return d.points.map(p => toPixel(p.time, p.price));
    if (d.type === 'H_LINE') {
      const y = seriesRef.current.priceToCoordinate(d.points[0].price);
      return y != null ? [{ x: canvasRef.current.width / 2, y: Math.round(y) }] : [];
    }
    if (d.type === 'V_LINE') {
      const x = chartRef.current.timeScale().timeToCoordinate(d.points[0].time);
      return x != null ? [{ x: Math.round(x), y: canvasRef.current.height / 2 }] : [];
    }
    if (d.type === 'TEXT') return [toPixel(d.points[0].time, d.points[0].price)];
    return [];
  };

  // Applies a single handle drag (px/py = new pixel position) and returns
  // an updated Drawing without mutating the original.
  const applyHandleDrag = (d: Drawing, handleIdx: number, px: number, py: number): Drawing => {
    if (d.type === 'H_LINE') {
      const price = seriesRef.current?.coordinateToPrice(py);
      if (price == null) return d;
      return { ...d, points: [{ ...d.points[0], price }] };
    }
    if (d.type === 'V_LINE') {
      const time = chartRef.current?.timeScale().coordinateToTime(px) as string | null;
      if (!time) return d;
      return { ...d, points: [{ time, price: d.points[0].price }] };
    }
    // Default: move the corresponding chart point
    const pt = toChartCoords(px, py);
    if (!pt) return d;
    const points = [...d.points];
    points[handleIdx] = pt;
    return { ...d, points };
  };

  // ── Canvas mouse handlers ─────────────────────────────────────────────
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const { px, py } = getEventCoords(e);
    mousePixel.current = { x: px, y: py };

    if (activeTool === 'CURSOR') {
      if (draggingHandle.current && isMouseDown.current && dragPreviewRef.current) {
        dragPreviewRef.current = applyHandleDrag(dragPreviewRef.current, draggingHandle.current.handleIdx, px, py);
      }
      redrawCanvas();
      return;
    }

    if (activeTool === 'FREEHAND' && isMouseDown.current) {
      const pt = toChartCoords(px, py);
      if (pt) freehandPoints.current.push(pt);
    }
    redrawCanvas();
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const { px, py } = getEventCoords(e);

    if (activeTool === 'CURSOR') {
      // Hit-test every drawing's handles; start drag if within 10 px
      const HIT = 10;
      for (const d of drawingsRef.current) {
        const handles = getCursorHandles(d);
        for (let i = 0; i < handles.length; i++) {
          const h = handles[i];
          if (!h) continue;
          if (Math.hypot(px - h.x, py - h.y) <= HIT) {
            draggingHandle.current = { drawingId: d.id, handleIdx: i };
            dragPreviewRef.current = d; // start from committed state
            isMouseDown.current = true;
            return;
          }
        }
      }
      return;
    }

    isMouseDown.current = true;
    if (activeTool === 'RECTANGLE') {
      rectStartPx.current = { x: px, y: py };
    }
    if (activeTool === 'FREEHAND') {
      freehandPoints.current = [];
      const pt = toChartCoords(px, py);
      if (pt) freehandPoints.current.push(pt);
    }
  };

  const commitDrag = () => {
    if (dragPreviewRef.current) {
      const preview = dragPreviewRef.current;
      setDrawings(prev => prev.map(d => d.id === preview.id ? preview : d));
    }
    draggingHandle.current = null;
    dragPreviewRef.current = null;
    isMouseDown.current = false;
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (activeTool === 'CURSOR') { commitDrag(); redrawCanvas(); return; }
    if (!isMouseDown.current) return;
    isMouseDown.current = false;
    const { px, py } = getEventCoords(e);
    const pt = toChartCoords(px, py);

    if (activeTool === 'RECTANGLE' && rectStartPx.current) {
      const startPt = toChartCoords(rectStartPx.current.x, rectStartPx.current.y);
      if (startPt && pt) {
        addDrawing({ type: 'RECTANGLE', points: [startPt, pt] });
      }
      rectStartPx.current = null;
    }

    if (activeTool === 'FREEHAND' && freehandPoints.current.length > 1) {
      addDrawing({ type: 'FREEHAND', points: [...freehandPoints.current] });
      freehandPoints.current = [];
    }
    redrawCanvas();
  };

  const handleMouseLeave = () => {
    if (activeTool === 'CURSOR') { commitDrag(); redrawCanvas(); return; }
    if (!isMouseDown.current) return;
    isMouseDown.current = false;
    if (activeTool === 'FREEHAND' && freehandPoints.current.length > 1) {
      addDrawing({ type: 'FREEHAND', points: [...freehandPoints.current] });
      freehandPoints.current = [];
    }
    if (activeTool === 'RECTANGLE') {
      rectStartPx.current = null;
    }
    redrawCanvas();
  };

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (activeTool === 'CURSOR' || activeTool === 'RECTANGLE' || activeTool === 'FREEHAND') return;
    const { px, py } = getEventCoords(e);

    // H_LINE — only needs price (y); time is stored but irrelevant for rendering
    if (activeTool === 'H_LINE') {
      if (!seriesRef.current) return;
      const price = seriesRef.current.coordinateToPrice(py);
      if (price === null) return;
      const anyTime = chartStock!.candles![0].time as string;
      addDrawing({ type: 'H_LINE', points: [{ time: anyTime, price }] });
      return;
    }

    // TEXT — click sets position, floating input appears
    if (activeTool === 'TEXT') {
      const pt = toChartCoords(px, py);
      if (!pt) return;
      setPendingText({ pos: pt, px, py });
      setTextValue('');
      setTimeout(() => textInputRef.current?.focus(), 50);
      return;
    }

    const pt = toChartCoords(px, py);
    if (!pt) return;

    if (activeTool === 'V_LINE') {
      addDrawing({ type: 'V_LINE', points: [pt] });
      return;
    }

    if (activeTool === 'H_RAY_RIGHT' || activeTool === 'H_RAY_LEFT') {
      addDrawing({ type: activeTool, points: [pt] });
      return;
    }

    // TREND_LINE / ARROW — 2 clicks
    if (activeTool === 'TREND_LINE' || activeTool === 'ARROW') {
      if (inProgressPoints.current.length === 0) {
        inProgressPoints.current = [pt];
        redrawCanvas();
      } else {
        addDrawing({ type: activeTool, points: [inProgressPoints.current[0], pt] });
        inProgressPoints.current = [];
      }
      return;
    }

    // ARC — 3 clicks: start → end → apex (stored as chart coordinate)
    if (activeTool === 'ARC') {
      if (inProgressPoints.current.length < 2) {
        inProgressPoints.current = [...inProgressPoints.current, pt];
        redrawCanvas();
      } else {
        // 3rd click = apex stored as chart coordinate
        addDrawing({
          type: 'ARC',
          points: [inProgressPoints.current[0], inProgressPoints.current[1], pt],
        });
        inProgressPoints.current = [];
      }
      return;
    }
  };

  const addDrawing = ({ type, points, text }: {
    type: DrawingTool;
    points: ChartPoint[];
    text?: string;
  }) => {
    const d: Drawing = {
      id: `${Date.now()}-${Math.random()}`,
      type,
      points,
      color:     activeColor,
      lineWidth,
      text,
    };
    setDrawings(prev => [...prev, d]);
  };

  const deleteDrawing = (id: string) => {
    setDrawings(prev => prev.filter(d => d.id !== id));
  };

  const clearAll = () => {
    setDrawings([]);
    inProgressPoints.current = [];
    freehandPoints.current   = [];
    redrawCanvas();
  };

  const toolLabel = (type: DrawingTool) =>
    TOOLS.find(t => t.id === type)?.label ?? type;

  const change        = priceData.close - priceData.prevClose;
  const changePercent = priceData.prevClose > 0 ? (change / priceData.prevClose) * 100 : 0;
  const isPositive    = change >= 0;
  const isBullish     = priceData.close >= priceData.open;

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="acd-page">
      {/* Page header */}
      <div className="acd-page-header">
        <button className="back-btn" onClick={() => navigate(-1)}><FaArrowLeft /></button>
        <h2>Chart Drawing Tool</h2>
      </div>

      <div className="acd-layout">
        {/* ── Left: screener selector + stock list ── */}
        <div className="acd-stock-list">
          <select
            className="acd-screener-select"
            value={selectedScreener}
            onChange={e => setSelectedScreener(e.target.value)}
          >
            {screeners.map(s => (
              <option key={s.code} value={s.code}>{s.name}</option>
            ))}
          </select>
          {loadingStocks && <div className="acd-stock-loading">Loading...</div>}
          {stocks.map(s => (
            <div
              key={s.id}
              className={`acd-stock-item ${selectedStock?.id === s.id ? 'active' : ''}`}
              onClick={() => setSelectedStock(s)}
            >
              <div className="acd-stock-symbol">{s.symbol}</div>
              <div className="acd-stock-name">{s.stockName}</div>
              <div className="acd-stock-price">₹{s.capturedPrice?.toFixed(2)}</div>
            </div>
          ))}
        </div>

        {/* ── Right: toolbar + chart ── */}
        <div className="acd-chart-area">

          {/* Toolbar */}
          <div className="acd-toolbar">
            <div className="acd-tools">
              {TOOLS.map(t => (
                <button
                  key={t.id}
                  className={`acd-tool-btn ${activeTool === t.id ? 'active' : ''}`}
                  title={`${t.label} — ${t.tip}`}
                  onClick={() => {
                    setActiveTool(t.id);
                    inProgressPoints.current = [];
                    freehandPoints.current   = [];
                    setPendingText(null);
                    setTextValue('');
                  }}
                >
                  {t.icon}
                  <span className="acd-tool-label">{t.label}</span>
                </button>
              ))}
            </div>

            <div className="acd-toolbar-divider" />

            {/* Color swatches */}
            <div className="acd-colors">
              {COLORS.map(c => (
                <button
                  key={c}
                  className={`acd-color-swatch ${activeColor === c ? 'active' : ''}`}
                  style={{ background: c }}
                  onClick={() => setActiveColor(c)}
                />
              ))}
            </div>

            <div className="acd-toolbar-divider" />

            {/* Line width */}
            <div className="acd-widths">
              {[1, 1.5, 2, 3].map(w => (
                <button
                  key={w}
                  className={`acd-width-btn ${lineWidth === w ? 'active' : ''}`}
                  onClick={() => setLineWidth(w)}
                >
                  <span className="acd-width-indicator" style={{ height: Math.max(1, w * 2) }} />
                </button>
              ))}
            </div>

            <div className="acd-toolbar-divider" />

            <button className="acd-clear-btn" onClick={clearAll}>Clear All</button>

            <div className="acd-toolbar-divider" />

            <button
              className={`acd-save-btn ${savedOk ? 'saved' : ''}`}
              onClick={handleSave}
              disabled={saving || !selectedStock}
              title="Save drawings to database"
            >
              {savedOk ? <FaCheckCircle /> : <FaSave />}
              {saving ? 'Saving…' : savedOk ? 'Saved' : 'Save'}
            </button>
          </div>

          {/* Chart + Canvas */}
          {!selectedStock ? (
            <div className="acd-no-stock">Select a stock from the list</div>
          ) : loadingCandles ? (
            <div className="acd-no-stock">Loading chart for {selectedStock.symbol}…</div>
          ) : !chartStock?.candles?.length ? (
            <div className="acd-no-stock">No candle data for {selectedStock.symbol}</div>
          ) : (
            <div className="acd-chart-wrapper">
              <div className="acd-chart-title-row">
                <div className="acd-chart-title">
                  {chartStock.symbol} · {chartStock.stockName} · ₹{chartStock.capturedPrice?.toFixed(2)}
                </div>
                <div className="acd-price-info">
                  <span className="acd-price-pair"><span className="acd-price-label">O</span><span className={`acd-price-value ${isBullish ? 'bullish' : 'bearish'}`}>{priceData.open.toFixed(2)}</span></span>
                  <span className="acd-price-pair"><span className="acd-price-label">H</span><span className={`acd-price-value ${isBullish ? 'bullish' : 'bearish'}`}>{priceData.high.toFixed(2)}</span></span>
                  <span className="acd-price-pair"><span className="acd-price-label">L</span><span className={`acd-price-value ${isBullish ? 'bullish' : 'bearish'}`}>{priceData.low.toFixed(2)}</span></span>
                  <span className="acd-price-pair"><span className="acd-price-label">C</span><span className={`acd-price-value ${isBullish ? 'bullish' : 'bearish'}`}>{priceData.close.toFixed(2)}</span></span>
                  <span className={`acd-price-change ${isPositive ? 'positive' : 'negative'}`}>
                    {isPositive ? '+' : ''}{change.toFixed(2)} ({isPositive ? '+' : ''}{changePercent.toFixed(2)}%)
                  </span>
                </div>
              </div>
              <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.09)' }}>
                <div ref={chartContainerRef} className="acd-chart-container" />
                <canvas
                  ref={canvasRef}
                  className="acd-drawing-canvas"
                  style={{
                    cursor: activeTool === 'CURSOR' ? 'default' : activeTool === 'TEXT' ? 'text' : 'crosshair',
                    pointerEvents: activeTool === 'CURSOR' ? 'none' : 'auto',
                    zIndex: 10,
                  }}
                  onClick={handleClick}
                  onMouseMove={handleMouseMove}
                  onMouseDown={handleMouseDown}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseLeave}
                />
                {/* Floating text input — appears at clicked position */}
                {pendingText && (
                  <input
                    ref={textInputRef}
                    className="acd-text-input"
                    style={{
                      left:  pendingText.px,
                      top:   pendingText.py - 18,
                      color: activeColor,
                      fontSize: 10 + lineWidth * 3,
                    }}
                    value={textValue}
                    placeholder="Type, Enter to confirm"
                    onChange={e => setTextValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && textValue.trim()) {
                        addDrawing({ type: 'TEXT', points: [pendingText.pos], text: textValue.trim() });
                        setPendingText(null);
                        setTextValue('');
                      } else if (e.key === 'Escape') {
                        setPendingText(null);
                        setTextValue('');
                      }
                    }}
                    onBlur={() => {
                      if (textValue.trim()) {
                        addDrawing({ type: 'TEXT', points: [pendingText.pos], text: textValue.trim() });
                      }
                      setPendingText(null);
                      setTextValue('');
                    }}
                  />
                )}
              </div>

              {/* Drawings list */}
              {drawings.length > 0 && (
                <div className="acd-drawings-list">
                  <span className="acd-drawings-list-title">Drawings ({drawings.length})</span>
                  {drawings.map(d => (
                    <div key={d.id} className="acd-drawing-chip">
                      <span className="acd-drawing-chip-dot" style={{ background: d.color }} />
                      {toolLabel(d.type)}
                      <button className="acd-drawing-chip-del" onClick={() => deleteDrawing(d.id)}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminChartDrawing;
